<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- Hero -->
            <section class="mkt-hero-light" style="padding:80px 0 40px;">
                <div class="mkt-container text-center">
                    <h1 class="text-h2 text-weight-bold q-mb-md" style="color:#0F172A;">Support</h1>
                    <p class="text-subtitle1" style="max-width:600px;margin:0 auto;color:#475569;">
                        Questions, issues, or feedback? We're here to help.
                    </p>
                </div>
            </section>

            <!-- Contact Options -->
            <section class="reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0; background: #f5f7fa;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-lg justify-center">

                        <!-- Email Support -->
                        <div class="col-12 col-sm-6 col-md-4">
                            <q-card class="full-height text-center q-pa-lg" style="border-radius:16px;">
                                <q-icon name="email" size="48px" color="primary" class="q-mb-md" />
                                <div class="text-h6 text-weight-bold q-mb-sm">Email Support</div>
                                <div class="text-body2 text-grey-7 q-mb-md">
                                    Send us a message and we'll respond within one business day.
                                </div>
                                <!-- Email-address obfuscation: address never appears in static
                                     HTML or as a mailto: link. The Contact page (/contact) has
                                     the click-to-reveal/copy flow; Support points users there
                                     so address-harvesting scrapers never see a usable string. -->
                                <q-btn outline color="primary" no-caps
                                       label="Email Us"
                                       href="/contact"
                                       icon="email" />
                            </q-card>
                        </div>

                        <!-- Documentation -->
                        <div class="col-12 col-sm-6 col-md-4">
                            <q-card class="full-height text-center q-pa-lg" style="border-radius:16px;">
                                <q-icon name="menu_book" size="48px" color="teal" class="q-mb-md" />
                                <div class="text-h6 text-weight-bold q-mb-sm">Documentation</div>
                                <div class="text-body2 text-grey-7 q-mb-md">
                                    API reference, integration guides, and SDK documentation.
                                </div>
                                <q-btn outline color="teal" no-caps
                                       label="Developer Docs"
                                       href="/Home/Developers"
                                       icon="code" />
                            </q-card>
                        </div>

                        <!-- API Status -->
                        <div class="col-12 col-sm-6 col-md-4">
                            <q-card class="full-height text-center q-pa-lg" style="border-radius:16px;">
                                <q-icon name="dns" size="48px" color="orange" class="q-mb-md" />
                                <div class="text-h6 text-weight-bold q-mb-sm">Platform Status</div>
                                <div class="text-body2 text-grey-7 q-mb-md">
                                    Check current API availability and system health.
                                </div>
                                <q-btn outline color="orange" no-caps
                                       label="API Reference"
                                       href="/Docs/ApiReference"
                                       icon="api" />
                            </q-card>
                        </div>

                    </div>
                </div>
            </section>

            <!-- Contact Form -->
            <section class="support-form-section reveal-scale" v-intersection="revealCfg">
                <div class="support-bg-swoosh"></div>
                <div class="support-bg-circle support-bg-circle--1"></div>
                <div class="support-bg-circle support-bg-circle--2"></div>

                <div class="mkt-container" style="position: relative; z-index: 1;">
                    <div class="row justify-center">
                        <div class="col-12 col-md-8 col-lg-6">
                            <div class="text-center q-mb-lg">
                                <div class="support-icon-wrapper">
                                    <div class="support-icon-ring"></div>
                                    <q-icon name="chat" size="56px" color="primary" />
                                </div>
                                <div class="text-h4 text-weight-bold" style="color: #2d3344; letter-spacing: -0.02em;">Send Us a Message</div>
                                <div class="text-subtitle1 q-mt-sm" style="color: #4a5067; max-width: 400px; margin: 0 auto;">
                                    We typically respond within one business day.
                                </div>
                            </div>

                            <q-card class="support-card q-pa-lg">

                                <div v-if="submitted" class="text-center q-pa-xl">
                                    <q-icon name="check_circle" size="64px" color="positive" class="q-mb-md" />
                                    <div class="text-h5 text-weight-bold q-mb-sm">Message Sent</div>
                                    <div class="text-body1 text-grey-7 q-mb-lg">
                                        We'll get back to you as soon as possible.
                                    </div>
                                    <q-btn outline color="primary" label="Send Another Message" @click="submitted = false" no-caps />
                                </div>

                                <template v-else>
                                    <div class="column q-gutter-md">
                                        <q-input v-model="form.name" outlined label="Name *" :rules="[v => !!v || 'Required']" />
                                        <q-input v-model="form.email" outlined label="Email *" type="email" :rules="[v => !!v || 'Required']" />
                                        <q-select v-model="form.topic" outlined label="Topic" :options="topicOptions" emit-value map-options />
                                        <q-input v-model="form.message" outlined label="Message *" type="textarea" rows="5"
                                                 :rules="[v => !!v || 'Required']" />
                                        <q-btn color="primary" label="Send Message" no-caps unelevated size="lg"
                                               :loading="sending" :disable="!isFormValid"
                                               style="border-radius: 10px;"
                                               @click="submitForm" />
                                    </div>
                                </template>

                            </q-card>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Footer -->
            <section class="mkt-light q-pa-lg text-center reveal-fade-up" v-intersection="revealCfg" style="border-top:1px solid #E2E8F0;">
                <div class="text-body2" style="color:#64748B;">
                    &copy; {{ currentYear }} eSign Launchpad. All rights reserved.
                </div>
            </section>
        </q-page-container>
    </q-layout>
</template>

<style>
.mkt-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
@media (max-width: 599px) { .mkt-hide-mobile { display: none !important; } }

.support-form-section {
    background: linear-gradient(160deg, #eef3fa 0%, #f4f5f7 40%, #eef0f7 100%);
    padding: 20px 0 80px;
    position: relative;
    overflow: hidden;
}
.support-bg-swoosh {
    position: absolute; top: -30%; right: -20%; width: 70%; height: 80%;
    background: radial-gradient(ellipse at center, rgba(99, 102, 241, 0.05) 0%, transparent 70%);
    border-radius: 50%; pointer-events: none;
}
.support-bg-circle { position: absolute; border-radius: 50%; pointer-events: none; }
.support-bg-circle--1 {
    bottom: -10%; left: -10%; width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.03) 0%, transparent 70%);
}
.support-bg-circle--2 {
    top: 10%; left: 5%; width: 200px; height: 200px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.025) 0%, transparent 70%);
}
.support-icon-wrapper {
    position: relative; display: inline-flex; align-items: center;
    justify-content: center; margin-bottom: 8px;
}
.support-icon-ring {
    position: absolute; width: 100px; height: 100px; border-radius: 50%;
    border: 2px solid rgba(99, 102, 241, 0.12);
    animation: support-ring-pulse 2.5s ease-in-out infinite;
}
@keyframes support-ring-pulse {
    0%, 100% { transform: scale(1); opacity: 0.6; }
    50% { transform: scale(1.12); opacity: 0; }
}
.support-card {
    border-radius: 16px !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04) !important;
    border: 1px solid rgba(0, 0, 0, 0.04) !important;
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.92) !important;
}
</style>

<script>
import { logout } from '@services/authActions.js';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'SupportPage',
    mixins: [revealMixin],
    components: { MarketingNav },
    data() {
        return {
            isAuthenticated: typeof _AUTHENTICATED !== 'undefined' && _AUTHENTICATED === true,
            mobileNav: false,
            currentYear: new Date().getFullYear(),
            submitted: false,
            sending: false,
            form: {
                name: '',
                email: '',
                topic: 'general',
                message: ''
            },
            topicOptions: [
                { label: 'General Question', value: 'general' },
                { label: 'Technical Support', value: 'technical' },
                { label: 'Billing & Subscription', value: 'billing' },
                { label: 'API & Integration', value: 'api' },
                { label: 'Feature Request', value: 'feature' },
                { label: 'Bug Report', value: 'bug' },
                { label: 'Enterprise & Partnerships', value: 'enterprise' }
            ]
        };
    },
    computed: {
        isFormValid() {
            return this.form.name.trim() && this.form.email.trim() && this.form.message.trim();
        }
    },
    methods: {
        logout,
        async submitForm() {
            if (!this.isFormValid) return;
            this.sending = true;
            try {
                await fetch('/api/support/contact', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.form)
                }).then(r => { if (!r.ok) throw new Error('Failed'); });
                this.submitted = true;
            } catch {
                this.$q.notify({ type: 'negative', message: 'Failed to send message. Please email us directly.' });
            } finally {
                this.sending = false;
            }
        }
    }
};
</script>
